import Edit from "@mui/icons-material/Edit"
import {
    TextField,
    Stack,
    Autocomplete,
    CircularProgress,
    Typography,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { ReactElement, SyntheticEvent, useEffect, useState } from "react"
import { Control, FieldValues, UseFormSetValue } from "react-hook-form"

import { UK_POSTCODE_REGEX } from "../../../../constants/patterns"
import {
    TT_ADDRESS_SEARCH,
    TT_ADDRESS_SEARCH_MANUAL,
    TT_AMENDMENT_MODE_DISABLED,
    TT_POSTCODE_INPUT,
} from "../../../../constants/tooltips"
import { useScreenSize } from "../../../../hooks/useScreenSize"
import useAddress from "../../../../services/middleware/hooks/singleApplication/useAddress"
import {
    ITEM_BORDER_MULTIPLIER,
    GRID_SPACING,
} from "../../../../themes/defaultThemeParams"
import { noOptionsText } from "../../../../utils/general/generalUtils"
import AppButton from "../../../buttons/appButton"
import HelpIcon from "../../../helper/helpIcon"
import ValidatedTextField from "../../../inputs/validated/validatedTextField"
import { APPLICATION_SPACING } from "../formGenerator/formDispatcher"

export const HOUSE_NO_KW = "houseNumber"
export const ADDRESS_KW = "mainAddress"
export const POSTCODE_KW = "installationPostCode"

export const setAddressFields = (
    setValue: UseFormSetValue<FieldValues>,
    address: string | undefined,
    postalCode: string | undefined
): void => {
    setValue(ADDRESS_KW, address)
    setValue(POSTCODE_KW, postalCode)
}

interface AddressFormProps {
    setValue: UseFormSetValue<FieldValues>
    control: Control
    isDefaults: boolean
    label: string
    worldwide?: boolean
    disabled?: boolean
}

/**
 * AddressForm component for displaying and managing address form fields.
 * Note the address and postcode fields are deliberately mounted in the
 * `Collapse` to ensure they are caught but react-hook-form when the form is
 * submitted.
 */
const AddressForm = ({
    control,
    setValue,
    isDefaults,
    label,
    worldwide = false,
    disabled = false,
}: AddressFormProps): ReactElement => {
    const [searchTerm, setSearchTerm] = useState("")
    const [isNIPostCode, setIsNIPostCode] = useState(false)
    const [houseNo, setHouseNo] = useState("")
    const { isMob } = useScreenSize()
    const { addressId, isLoading: searchAddressLoading } = useAddress(
        searchTerm,
        houseNo,
        worldwide
    )
    // const hint = useRef("")
    const [hintInputValue, setHintInputValue] = useState("")
    const [hideAddressFields, setHideAddressFields] = useState(isDefaults)
    const [showButton, setShowButton] = useState(!isDefaults)

    useEffect(() => {
        setHideAddressFields(isDefaults)
        setShowButton(!isDefaults)
    }, [isDefaults])

    // called when user types in the address search field to fetch suggestions
    const handleSearch = (
        _event: SyntheticEvent<Element, Event>,
        value: string,
        reason: string
    ): void => {
        if (value) {
            setSearchTerm(value)
            setHintInputValue(value)
            setIsNIPostCode(value.toLowerCase().startsWith("bt"))
        }
        if (reason === "clear" || (reason === "input" && value === "")) {
            setSearchTerm("")
            setIsNIPostCode(false)
            setHintInputValue("")
            setHouseNo("")
        }
    }

    const extractAddressAndPostcode = (
        address: string
    ): { mainAddress: string; postcode: string } => {
        const parts = address.split(",")
        const postcode = parts.pop()?.trim() || ""
        const mainAddress = parts.join(",").trim()
        return { mainAddress, postcode }
    }
    // called when user selects an address from the autocomplete suggestions
    const handleSelect = (
        _event: SyntheticEvent<Element, Event>,
        value: { Text: string } | null
    ): void => {
        // when user selects an address, we extract the address and postcode from the Text field
        if (value !== null) {
            const { mainAddress, postcode } = extractAddressAndPostcode(
                value.Text
            )
            setAddressFields(setValue, mainAddress, postcode)
            setSearchTerm("")
            setHintInputValue(value.Text)
            setHouseNo("")
        }

        setHideAddressFields(true)
        setShowButton(false)
        setHouseNo("")
    }

    const displayAddressFields = (): void => {
        setHideAddressFields(true)
        setShowButton(false)
    }

    return (
        <>
            <Grid
                container
                rowSpacing={GRID_SPACING}
                columnSpacing={GRID_SPACING}
                spacing={GRID_SPACING}
                display="flex"
                alignItems="center"
            >
                <Grid md={3} sm={12} xs={12} padding={{ xs: 0, sm: 0, md: 0 }}>
                    <TextField
                        disabled={disabled || isNIPostCode === false}
                        label="House No. Northern Ireland Only"
                        required
                        placeholder="house no. Northern Ireland Only"
                        multiline={false}
                        onChange={(e) => setHouseNo(e.target.value)}
                        error={isNIPostCode && houseNo === ""}
                        value={houseNo}
                    />
                </Grid>

                <Grid md={9} sm={12} xs={12} padding={{ xs: 0, sm: 0, md: 2 }}>
                    <Stack direction="row" flexDirection="row" width="100%">
                        <Autocomplete
                            fullWidth
                            options={addressId ?? []}
                            loading={searchAddressLoading}
                            isOptionEqualToValue={(option, value) =>
                                option.Text === value.Text
                            }
                            getOptionLabel={(option) => String(option.Text)}
                            onChange={handleSelect}
                            disabled={disabled}
                            onInputChange={handleSearch}
                            inputValue={hintInputValue}
                            noOptionsText={noOptionsText(
                                searchTerm,
                                "Start typing to search for a post code...",
                                "Continue typing to search for a post code...",
                                "No address found. Please try another search or enter manually"
                            )}
                            renderOption={(props, option, { inputValue }) => {
                                const matches = match(
                                    String(option.Text),
                                    inputValue,
                                    {
                                        insideWords: true,
                                    }
                                )
                                const parts = parse(
                                    String(option.Text),
                                    matches
                                )
                                return (
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    <li {...props}>
                                        <div>
                                            {parts.map((part, index) => (
                                                <span
                                                    key={
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        part.text +
                                                        part.highlight +
                                                        index
                                                    }
                                                    style={{
                                                        fontWeight:
                                                            part.highlight
                                                                ? 700
                                                                : 400,
                                                    }}
                                                >
                                                    {part.text}
                                                </span>
                                            ))}
                                        </div>
                                    </li>
                                )
                            }}
                            renderInput={(params) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const {
                                    InputLabelProps,
                                    InputProps,
                                    ...otherParams
                                } = params
                                return (
                                    <TextField
                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                        {...otherParams}
                                        required
                                        label={label}
                                        placeholder="Start typing to search for Post Code..."
                                        InputProps={{
                                            ...InputProps,
                                            sx: {
                                                borderRadius:
                                                    ITEM_BORDER_MULTIPLIER,
                                            },
                                            endAdornment: (
                                                <>
                                                    {searchAddressLoading ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={20}
                                                        />
                                                    ) : null}
                                                    {
                                                        params.InputProps
                                                            .endAdornment
                                                    }
                                                </>
                                            ),
                                        }}
                                        onChange={(event) => {
                                            const newValue = event.target.value
                                            setHintInputValue(newValue)
                                        }}
                                    />
                                )
                            }}
                        />
                        {!isMob && (
                            <HelpIcon
                                tooltip={`${
                                    disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                                }${TT_ADDRESS_SEARCH}`}
                            />
                        )}
                    </Stack>
                </Grid>
                <Typography
                    fontSize={12}
                    marginTop={0.5}
                    marginBottom={1.5}
                    color="text.secondary"
                >
                    Enter a postcode to view suggested postcodes and
                    corresponding addresses. Enter manually if you experience
                    issues.
                </Typography>
            </Grid>

            {showButton && (
                <AppButton
                    onClick={displayAddressFields}
                    disabled={disabled}
                    variant="outlined"
                    color="primary"
                    endIcon={<Edit />}
                    sx={{
                        width: "auto",
                        alignSelf: "flex-start",
                    }}
                >
                    Enter address manually
                </AppButton>
            )}
            <div style={{ display: hideAddressFields ? "block" : "none" }}>
                <Stack spacing={APPLICATION_SPACING} marginTop={0.5}>
                    <ValidatedTextField
                        control={control}
                        disabled={disabled}
                        label="Address"
                        registerName={ADDRESS_KW}
                        required
                        placeholder="Address"
                        multiline={false}
                        loading={searchAddressLoading}
                        tooltip={`${
                            disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                        }${TT_ADDRESS_SEARCH_MANUAL}`}
                    />
                    <ValidatedTextField
                        control={control}
                        disabled={disabled}
                        label="Post Code"
                        registerName={POSTCODE_KW}
                        required
                        placeholder="Post Code"
                        multiline={false}
                        loading={searchAddressLoading}
                        tooltip={`${
                            disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                        }${TT_POSTCODE_INPUT}`}
                        customRules={
                            worldwide
                                ? undefined
                                : {
                                      pattern: {
                                          value: UK_POSTCODE_REGEX,
                                          message: "Invalid postcode",
                                      },
                                  }
                        }
                    />
                </Stack>
            </div>
        </>
    )
}

AddressForm.defaultProps = {
    worldwide: false,
    disabled: false,
}

export default AddressForm
